// src/modules/app-views/services/pages/Init/modals/DataImportModal/StepMapping.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect } from 'react';
import { Select, Tag, Flex, Space, Spin, Typography } from 'antd';
import { RobotOutlined, RightOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';

const { Text } = Typography;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const StepMappingContent = ({
  mappings,
  targetFieldOptions,
  aiLoading,
  onMappingChange,
  mappedCount,
  ignoredCount,
  importId,
  requestAiMapping,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Auto-trigger AI mapping when entering this step
  useEffect(() => {
    if (importId && mappings.length === 0) {
      requestAiMapping();
    }
  }, [importId, mappings.length, requestAiMapping]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (aiLoading) {
    return (
      <Flex vertical align="center" gap={16} style={{ padding: '40px 0' }}>
        <RobotOutlined style={{ fontSize: 48, color: '#1677ff' }} />
        <Spin size="large" />
        <Text>{translateX('data_import_ai_mapping_loading')}</Text>
      </Flex>
    );
  }

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
        <Text type="secondary">{translateX('data_import_mapping_description')}</Text>
        <Space>
          <Tag color="green">{mappedCount} {translateX('data_import_mapped_fields')}</Tag>
          <Tag color="default">{ignoredCount} {translateX('data_import_ignored_fields')}</Tag>
        </Space>
      </Flex>

      <div style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 4 }}>
        {mappings.map((mapping) => (
          <Flex
            key={mapping.sourceField}
            align="center"
            gap={8}
            style={{
              padding: '8px 12px',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            {/* Source field name */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text ellipsis strong style={{ fontSize: 13 }}>
                {mapping.sourceField}
              </Text>
            </div>

            {/* Arrow */}
            <RightOutlined style={{ color: '#bbb', flexShrink: 0 }} />

            {/* Target field select */}
            <Select
              style={{ flex: 1, minWidth: 180 }}
              placeholder={translateX('data_import_ignore_column')}
              value={mapping.targetField || undefined}
              onChange={(value) => onMappingChange(mapping.sourceField, value)}
              allowClear
              size="small"
              options={targetFieldOptions}
            />

            {/* Confidence badge */}
            {mapping.targetField && (
              <ConfidenceBadge confidence={mapping.confidence} />
            )}
          </Flex>
        ))}
      </div>
    </div>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const stepMapping = ({
  mappings,
  targetFields,
  aiLoading,
  onMappingChange,
  importId,
  requestAiMapping,
  saveMappings,
  mappedCount,
  ignoredCount,
}) => {
  const targetFieldOptions = targetFields.map(f => ({
    label: f.label || f.field,
    value: f.field,
  }));

  return {
    stepTitle: 'mapping',
    stepTooltip: 'data_import_mapping_description',
    content: () => (
      <StepMappingContent
        mappings={mappings}
        targetFieldOptions={targetFieldOptions}
        aiLoading={aiLoading}
        onMappingChange={onMappingChange}
        mappedCount={mappedCount}
        ignoredCount={ignoredCount}
        importId={importId}
        requestAiMapping={requestAiMapping}
      />
    ),
    onNext: async () => {
      if (aiLoading) return false;
      if (mappedCount === 0) {
        appService.message({ flag: 'w', text: 'required_field' });
        return false;
      }
      return await saveMappings();
    },
  };
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default stepMapping;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function ConfidenceBadge({ confidence }) {
  const percent = Math.round((confidence || 0) * 100);
  let color = 'red';

  if (percent >= 80) color = 'green';
  else if (percent >= 50) color = 'orange';

  return (
    <Tag color={color} style={{ minWidth: 40, textAlign: 'center', flexShrink: 0 }}>
      {percent}%
    </Tag>
  );
}
